import { PageHeaderWrapper, Text } from "@clipboard-health/ui-components";
import { getTheme } from "@clipboard-health/ui-theme";
import { Stack, ThemeProvider } from "@mui/material";
import { NotFoundPage } from "@src/appV2/Errors/NotFound";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { APP_V2_USER_EVENTS, useToast } from "@src/appV2/lib";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { useQueryClient } from "@tanstack/react-query";
// eslint-disable-next-line no-restricted-imports -- legacy, use @clipboard-health/date-time. See https://www.notion.so/BP-Date-Time-2ef8643321f48032ad80e8a5b30aa278 for more information
import { format } from "date-fns";
import { intersection } from "lodash";
import { type ReactElement } from "react";
import { useHistory } from "react-router-dom";

import { CalendarHeaderRow } from "../Calendar/HeaderRow";
import { LoadingButton } from "../components/LoadingButton";
import { PageFooterWrapper } from "../components/PageFooterWrapper";
import { PageHeaderTopRow } from "../components/PageHeaderTopRow";
import { PageWrapper } from "../components/PageWrapper";
import { MonthNavigationHeader } from "../Shift/Calendar/MonthNavigationHeader";
import { SHIFT_DISCOVERY_LIST_PATH } from "../ShiftDiscovery/paths";
import { PageContent } from "../WorkerShifts/components/PageContent";
import { useUpdateWorkerAvailabilities } from "./api/useUpdateWorkerAvailability";
import { WorkerAvailabilityForm } from "./components/WorkerAvailabilityForm/WorkerAvailabilityForm";
import { AVAILABILITIES_PATH } from "./constants";
import { useWorkerAvailabilityContext } from "./context";
import { useMonthRange } from "./useMonthRange";
import { getAvailableDateStringsFromCurrentDates } from "./utils";

export function WorkerAvailabilityPage(): ReactElement {
  const worker = useDefinedWorker();

  const agentAvailableQualifications = worker.licensesData?.map((item) => item.qualification) ?? [];
  const { qualifications } = useCbhFlag(CbhFeatureFlag.ENABLE_WORKER_AVAILABILITY, {
    defaultValue: { qualifications: [] },
  });
  const isWorkerAvailabilityEnabled =
    intersection(qualifications, agentAvailableQualifications).length > 0;

  const history = useHistory();
  const queryClient = useQueryClient();
  const { showErrorToast, showSuccessToast } = useToast();

  const { onPrevious, onNext, isFirstMonth, dateRange } = useMonthRange();

  const {
    currentDates,
    isLoading: isWorkerAvailabilityLoading,
    timeZone,
    calendarOptions,
    modifiedDates,
  } = useWorkerAvailabilityContext();

  const { isLoading: isSavingAvailabilities, mutate: updateAvailabilities } =
    useUpdateWorkerAvailabilities({
      onSuccess(_response) {
        void queryClient.invalidateQueries([AVAILABILITIES_PATH]);
        showSuccessToast("Your availability has been saved!");
        history.push(SHIFT_DISCOVERY_LIST_PATH);
      },
      onError() {
        showErrorToast("Something went wrong while saving your availabilities");
      },
    });

  useLogEffect(APP_V2_USER_EVENTS.AVAILABILITY_CALENDAR_VIEWED);

  if (!isWorkerAvailabilityEnabled) {
    return <NotFoundPage />;
  }

  return (
    <ThemeProvider theme={getTheme()}>
      <PageWrapper variant="tertiary">
        <PageHeaderWrapper variant="tertiary">
          <PageHeaderTopRow title="Availability" />
        </PageHeaderWrapper>

        <PageContent>
          <Stack spacing={4} height="100%">
            <Text textAlign="center" marginBottom={3}>
              Let workplaces know when you&apos;re available by tapping on the dates below.
              You&apos;ll be notified as soon as shifts are posted.
            </Text>

            <MonthNavigationHeader
              currentMonthDate={dateRange.startDate}
              isFirstMonth={isFirstMonth}
              isLastMonth={false}
              onPreviousMonth={onPrevious}
              onNextMonth={onNext}
            />

            <CalendarHeaderRow />

            <WorkerAvailabilityForm
              isSubmitting={isSavingAvailabilities}
              currentDate={dateRange.startDate}
            />
          </Stack>
        </PageContent>

        <PageFooterWrapper>
          <LoadingButton
            fullWidth
            isLoading={isSavingAvailabilities || isWorkerAvailabilityLoading}
            disabled={Object.values(modifiedDates).length === 0}
            variant="contained"
            onClick={() => {
              updateAvailabilities({
                tmz: timeZone,
                startDate: format(calendarOptions.minDate, "yyyy-MM-dd"),
                endDate: format(calendarOptions.maxDate, "yyyy-MM-dd"),
                availableDates: getAvailableDateStringsFromCurrentDates(currentDates),
              });
            }}
          >
            Save
          </LoadingButton>
        </PageFooterWrapper>
      </PageWrapper>
    </ThemeProvider>
  );
}
